import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { MapPin, ArrowLeft, ArrowRight, PlusCircle } from "lucide-react";
import { PackageData } from "@/pages/CreatePackage";
import { toast } from "sonner";
import { useTouristTours, TouristTourFromDB } from "@/hooks/useTouristTours";
import { Skeleton } from "@/components/ui/skeleton";
import { AddTourDialog } from "./ToursStep/AddTourDialog";
import { PackageToursList } from "./ToursStep/PackageToursList";
import { ProgressSteps } from "./ProgressSteps";
interface ToursStepProps {
  data: PackageData;
  onUpdate: (data: Partial<PackageData>) => void;
  onNext: () => void;
  onPrev: () => void;
  completedSteps?: number[];
  onStepClick?: (step: number) => void;
  isStepRequired?: (step: number) => boolean;
}
export const ToursStep = ({
  data,
  onUpdate,
  onNext,
  onPrev,
  completedSteps = [],
  onStepClick = () => {},
  isStepRequired = () => true
}: ToursStepProps) => {
  const {
    data: tourTemplates,
    isLoading
  } = useTouristTours();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedTour, setSelectedTour] = useState<TouristTourFromDB | null>(null);
  const handleOpenDialog = (tour: TouristTourFromDB) => {
    setSelectedTour(tour);
    setIsDialogOpen(true);
  };
  const handleAddTour = (newTour: PackageData['tours'][0]) => {
    const updatedTours = [...data.tours, newTour];
    const updatePayload: Partial<PackageData> = {
      tours: updatedTours
    };
    if (selectedTour && selectedTour.vehicle_types) {
      const newTransportItem: PackageData['transport'][0] = {
        type: 'tour_transport',
        vehicle: selectedTour.vehicle_types.name_ar || 'سيارة سياحية',
        from: 'الفندق',
        to: newTour.name,
        date: newTour.date,
        price: 0 // Price is in tour, so transport price is 0
      };
      updatePayload.transport = [...data.transport, newTransportItem];
    }
    onUpdate(updatePayload);
    toast.success(`تمت إضافة جولة "${newTour.name}" بنجاح.`);
  };
  const handleRemoveTour = (index: number) => {
    const removedTour = data.tours[index];
    const updatedTours = data.tours.filter((_, i) => i !== index);
    const updatePayload: Partial<PackageData> = {
      tours: updatedTours
    };
    const transportIndex = data.transport.findIndex(t => t.type === 'tour_transport' && t.to === removedTour.name && t.date === removedTour.date);
    if (transportIndex > -1) {
      const updatedTransport = data.transport.filter((_, i) => i !== transportIndex);
      updatePayload.transport = updatedTransport;
    }
    onUpdate(updatePayload);
    toast.info(`تمت إزالة جولة "${removedTour.name}".`);
  };
  return <>
      <Card className="w-full">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MapPin className="w-5 h-5" />
            الجولات السياحية
          </CardTitle>
          
        </CardHeader>
        <CardContent className="space-y-6">
          
          <div className="space-y-4">
            
            {isLoading ? Array.from({
            length: 3
          }).map((_, i) => <Skeleton key={i} className="h-24 w-full" />) : <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {tourTemplates?.map(tour => <Card key={tour.id} className="flex flex-col">
                    <CardHeader className="flex-grow">
                      <CardTitle className="text-base">{tour.name_ar}</CardTitle>
                      {tour.description_ar && <CardDescription className="text-xs pt-1">{tour.description_ar}</CardDescription>}
                    </CardHeader>
                    <CardContent className="flex justify-between items-center">
                      <span className="text-sm font-semibold text-green-600">{tour.price ? `${tour.price} ${tour.currency}` : 'غير مسعر'}</span>
                      <Button size="sm" variant="outline" onClick={() => handleOpenDialog(tour)}>
                        <PlusCircle className="ml-2 h-4 w-4" />
                        إضافة
                      </Button>
                    </CardContent>
                  </Card>)}
              </div>}
             {tourTemplates?.length === 0 && !isLoading && <p className="text-sm text-gray-500 text-center py-4">لا توجد جولات معرفة. يمكنك إضافتها من قسم المواصلات {'>'} الجولات السياحية.</p>}
          </div>

          <div className="border-t my-6"></div>

          <PackageToursList tours={data.tours} onRemoveTour={handleRemoveTour} />

          <div className="flex justify-between pt-6 border-t mt-6">
            <Button variant="outline" onClick={onPrev}>
              <ArrowRight className="w-4 h-4 ml-2" />
              السابق
            </Button>
            <Button onClick={onNext} className="bg-blue-600 hover:bg-blue-700">
              التالي
              <ArrowLeft className="w-4 h-4 mr-2" />
            </Button>
          </div>
        </CardContent>
      </Card>
      <AddTourDialog isOpen={isDialogOpen} onOpenChange={setIsDialogOpen} tourTemplate={selectedTour} onAddTour={handleAddTour} />
      
      {/* Progress Steps */}
      <ProgressSteps 
        currentStep={5} 
        completedSteps={completedSteps} 
        onStepClick={onStepClick} 
        isStepRequired={isStepRequired} 
      />
    </>;
};